/**
 * Auth state mutation hook — wraps authService calls with Jotai atom updates,
 * Mantine notifications, and router navigation.
 *
 * Every auth action follows the same pattern:
 *   call service -> extract token/user -> set atoms -> notify -> navigate
 *
 * Returns { success, error? } so callers can react without try/catch.
 * @module useAuth
 */

import { useAtom } from 'jotai'
import { useAtomValue } from 'jotai'
import { useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { authTokenAtom, authUserAtom, isAuthenticatedAtom, isProvisionalAtom } from '../atoms/authAtoms'
import { lastPlayedStoryAtom } from '../atoms/playerAtoms'
import { authService } from '../services/authService'
import { notifications } from '@mantine/notifications'

/**
 * Extract token and user from auth API responses.
 *
 * Handles 4 response shapes because Rails Devise + devise-jwt returns
 * different structures depending on endpoint and configuration:
 *   1. { token, user }         — standard login
 *   2. { token, data }         — some Devise configs wrap user in `data`
 *   3. { data: { token, user } } — nested envelope
 *   4. { authorization: { token }, data } — Devise-jwt with authorization header mirrored in body
 *
 * @param {Object} data - Raw response body from an auth endpoint
 * @returns {{ token: string, user: Object }|null} Extracted credentials, or null if unrecognized
 */
function extractTokenAndUser(data) {
  if (data.token && data.user) return { token: data.token, user: data.user }
  if (data.token && data.data) return { token: data.token, user: data.data }
  if (data.data?.token && data.data?.user) return { token: data.data.token, user: data.data.user }
  if (data.authorization?.token && data.data) return { token: data.authorization.token, user: data.data }
  return null
}

export function useAuth() {
  const [token, setToken] = useAtom(authTokenAtom)
  const [user, setUser] = useAtom(authUserAtom)
  const [isAuthenticated] = useAtom(isAuthenticatedAtom)
  const isProvisional = useAtomValue(isProvisionalAtom)
  const [, setLastPlayedStory] = useAtom(lastPlayedStoryAtom)
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const consumeReturnTo = (fallback = '/') => {
    const returnTo = sessionStorage.getItem('bw_returnTo')
    if (returnTo) sessionStorage.removeItem('bw_returnTo')
    return returnTo || fallback
  }

  const login = async (email, password, navigateTo) => {
    try {
      const data = await authService.login(email, password)
      const extracted = extractTokenAndUser(data)

      if (!extracted) throw new Error('Invalid response format from server')

      setToken(extracted.token)
      setUser(extracted.user)
      queryClient.invalidateQueries()

      notifications.show({
        title: 'Welcome back',
        message: 'Logged in successfully',
        color: 'cyan',
      })

      navigate(navigateTo || consumeReturnTo())
      return { success: true }
    } catch (error) {
      const message = error.response?.data?.error ||
        error.response?.data?.message ||
        error.message ||
        'Login failed'

      notifications.show({
        title: 'Login Failed',
        message,
        color: 'red',
      })

      return { success: false, error: message }
    }
  }

  const signup = async (email, password, passwordConfirmation, name) => {
    try {
      const data = await authService.signup(email, password, passwordConfirmation, name)
      const extracted = extractTokenAndUser(data)

      if (!extracted) throw new Error('Invalid response format from server')

      setToken(extracted.token)
      setUser(extracted.user)
      queryClient.invalidateQueries()

      notifications.show({
        title: 'Account created',
        message: 'Welcome to Bubot Worlds!',
        color: 'cyan',
      })

      navigate(consumeReturnTo())
      return { success: true }
    } catch (error) {
      const message = error.response?.data?.error ||
        error.response?.data?.message ||
        error.message ||
        'Signup failed'

      notifications.show({
        title: 'Signup Failed',
        message,
        color: 'red',
      })

      return { success: false, error: message }
    }
  }

  const requestVerificationCode = async (email) => {
    try {
      const data = await authService.requestVerificationCode(email)
      return { success: true, status: data.status }
    } catch (error) {
      const message = error.response?.data?.error ||
        error.message ||
        'Failed to send verification code'
      return { success: false, error: message }
    }
  }

  const verifyCode = async (email, code, navigateTo) => {
    try {
      const data = await authService.verifyCode(email, code)
      const extracted = extractTokenAndUser(data)

      if (!extracted) throw new Error('Invalid response format from server')

      setToken(extracted.token)
      setUser(extracted.user)
      queryClient.invalidateQueries()

      navigate(navigateTo || consumeReturnTo())
      return { success: true }
    } catch (error) {
      const message = error.response?.data?.error ||
        error.message ||
        'Verification failed'
      return { success: false, error: message }
    }
  }

  const upgradeAccount = async (name, password, passwordConfirmation) => {
    try {
      const data = await authService.upgradeAccount(name, password, passwordConfirmation)
      // Backend may issue a new JWT on upgrade (Devise-JWT re-signs after
      // credential changes). Extract and store it so subsequent requests
      // use the upgraded token instead of the old provisional one.
      const extracted = extractTokenAndUser(data)
      if (extracted) {
        setToken(extracted.token)
        setUser(extracted.user)
      } else {
        // Fallback: if response doesn't include a new token, update user locally
        setUser((prev) => ({ ...prev, name, provisional: false }))
      }

      notifications.show({
        title: 'Account upgraded',
        message: 'Your account is now fully set up!',
        color: 'cyan',
      })

      return { success: true }
    } catch (error) {
      const message = error.response?.data?.error ||
        error.message ||
        'Upgrade failed'

      return { success: false, error: message }
    }
  }

  // Roster claim-link onboarding. Consume the token, store the issued JWT +
  // user, then route by the backend landing hint: straight into the single
  // accessible story, otherwise to the teams list (the cohort's home).
  const claim = async (token, { email, password, passwordConfirmation, name }) => {
    try {
      const data = await authService.claim(token, { email, password, passwordConfirmation, name })
      const extracted = extractTokenAndUser(data)

      if (!extracted) throw new Error('Invalid response format from server')

      setToken(extracted.token)
      setUser(extracted.user)
      queryClient.invalidateQueries()

      notifications.show({
        title: 'Welcome aboard',
        message: 'Your account is ready.',
        color: 'cyan',
      })

      // Team members land on their team dashboard (team play must launch from
      // there); a lone story goes straight in; otherwise the teams list.
      const landing = data.landing
      if (landing?.type === 'team' && landing.team_id) {
        navigate(`/teams/${landing.team_id}`)
      } else if (landing?.type === 'story' && landing.story_id) {
        navigate(`/play/${landing.story_id}`)
      } else {
        navigate('/teams')
      }
      return { success: true }
    } catch (error) {
      const message = error.response?.data?.error ||
        error.response?.data?.message ||
        error.message ||
        'Could not complete sign-up'

      notifications.show({
        title: 'Sign-up Failed',
        message,
        color: 'red',
      })

      return { success: false, error: message }
    }
  }

  const requestPasswordReset = async (email) => {
    try {
      await authService.requestPasswordReset(email)
      return { success: true }
    } catch (error) {
      const message = error.response?.data?.status?.message ||
        error.message ||
        'Failed to send reset instructions'
      return { success: false, error: message }
    }
  }

  const resetPassword = async (token, password, passwordConfirmation) => {
    try {
      const data = await authService.resetPassword(token, password, passwordConfirmation)
      const extracted = extractTokenAndUser(data)

      if (!extracted) throw new Error('Invalid response format from server')

      setToken(extracted.token)
      setUser(extracted.user)

      notifications.show({
        title: 'Password reset',
        message: 'Your password has been updated successfully',
        color: 'cyan',
      })

      navigate('/')
      return { success: true }
    } catch (error) {
      const message = error.response?.data?.status?.message ||
        error.message ||
        'Failed to reset password'
      return { success: false, error: message }
    }
  }

  const logout = async () => {
    await authService.logout()
    setToken(null)
    setUser(null)
    setLastPlayedStory(null)
    queryClient.removeQueries()
    navigate('/')
  }

  return {
    token,
    user,
    isAuthenticated,
    isProvisional,
    login,
    signup,
    requestVerificationCode,
    verifyCode,
    upgradeAccount,
    claim,
    requestPasswordReset,
    resetPassword,
    logout,
  }
}
